import { AUTH_API_BASE_URL } from '../../config/backendEndpoints';
import { clientTokenCache } from './ClientTokenCache';
import { fetchWithTimeout } from './fetchWithTimeout';

class DSPAuthError extends Error {
  constructor(message, status = 500, details = null) {
    super(message);
    this.name = 'DSPAuthError';
    this.status = status;
    this.details = details;
  }
}

const parseJSONSafe = async response => {
  try {
    return await response.json();
  } catch (_error) {
    return null;
  }
};

const toError = async (response, fallbackMessage) => {
  const payload = await parseJSONSafe(response);
  const message = payload?.error || fallbackMessage;
  return new DSPAuthError(message, response.status, payload);
};

const withAuthBaseUrl = endpoint => {
  if (endpoint.startsWith('http')) {
    return endpoint;
  }

  return `${AUTH_API_BASE_URL}${endpoint}`;
};

const SESSION_ENDPOINTS = new Set(['/auth/session', '/auth/login', '/auth/register']);

class DSPAuthService {
  async request(endpoint, options = {}) {
    const response = await fetchWithTimeout(withAuthBaseUrl(endpoint), {
      credentials: 'include',
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });

    if (!response.ok) {
      if (response.status === 401 && !SESSION_ENDPOINTS.has(endpoint)) {
        const payload = await parseJSONSafe(response.clone());
        const code = payload?.code || 'session_expired';
        clientTokenCache.invalidate();
        clientTokenCache.notifySessionExpired(code);
        throw new DSPAuthError(code, 401, payload);
      }
      throw await toError(response, 'Authentication request failed');
    }

    if (response.status === 204) {
      return null;
    }

    return parseJSONSafe(response);
  }

  async login(email, password) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async register(payload) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async logout() {
    return this.request('/auth/logout', {
      method: 'POST',
    });
  }

  async requestPasswordReset(email) {
    return this.request('/auth/request-password-reset', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async updatePassword(payload) {
    return this.request('/auth/update-password', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getSession() {
    return this.request('/auth/session', {
      method: 'GET',
    });
  }

  async reportRejectedClientToken(clientToken) {
    return this.request('/auth/token/rejected', {
      method: 'POST',
      body: JSON.stringify({ clientToken }),
    });
  }

  async getClientToken() {
    return this.request('/auth/token', {
      method: 'GET',
    });
  }
}

const dspAuthService = new DSPAuthService();

export { DSPAuthError, dspAuthService };
export default DSPAuthService;
